import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

const toInt = (value) => Math.max(0, Number.parseInt(value, 10) || 0);

// Counting dialog: every declared item starts as "matches"; the receiver edits only the differences.
export default function ReceiveCountDialog({ pallet, onClose, onSubmit }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [unexpected, setUnexpected] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const tenantName = (id) => pallet?.works.find(w => w.tenant_id === id)?.tenant_name || "";

  useEffect(() => {
    if (!pallet) return;
    setItems([]); setCounts({}); setUnexpected([]); setLoadFailed(false); setBusy(false);
    axios.get(`${API}/pallets/${pallet.id}/items`, ax).then(({ data }) => {
      setItems(data);
      setCounts(Object.fromEntries(data.map(i => [i.id, { good: String(i.quantity), damaged: "0", note: "" }])));
    }).catch(() => setLoadFailed(true));
  }, [pallet]);

  const setCount = (id, patch) => setCounts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  const allMatch = () => setCounts(Object.fromEntries(items.map(i => [i.id, { good: String(i.quantity), damaged: "0", note: "" }])));
  const addUnexpected = () => setUnexpected(rows => [...rows, {
    key: Date.now() + rows.length, tenant_id: pallet?.works?.[0]?.tenant_id || "", asin: "", name: "", good: "1", damaged: "0", note: "", results: [],
  }]);
  const patchUnexpected = (key, patch) => setUnexpected(rows => rows.map(r => (r.key === key ? { ...r, ...patch } : r)));

  const searchProduct = async (key, value) => {
    patchUnexpected(key, { name: value, asin: "" });
    if (value.trim().length < 2) { patchUnexpected(key, { results: [] }); return; }
    try {
      const { data } = await axios.get(`${API}/products`, { ...ax, params: { q: value.trim() } });
      patchUnexpected(key, { results: data.slice(0, 6) });
    } catch {
      patchUnexpected(key, { results: [] });
    }
  };

  const unexpectedValid = unexpected.every(r => r.asin && r.tenant_id && toInt(r.good) + toInt(r.damaged) >= 1);

  const submit = async () => {
    const lines = items.flatMap(i => {
      const c = counts[i.id];
      const good = toInt(c.good), damaged = toInt(c.damaged);
      if (good === i.quantity && damaged === 0) return [];
      return [{ item_id: i.id, good, damaged, ...(c.note.trim() ? { note: c.note.trim() } : {}) }];
    });
    const extras = unexpected.map(r => ({
      tenant_id: r.tenant_id, asin: r.asin, good: toInt(r.good), damaged: toInt(r.damaged),
      ...(r.note.trim() ? { note: r.note.trim() } : {}),
    }));
    setBusy(true);
    const ok = await onSubmit({ lines, unexpected: extras });
    if (!ok) setBusy(false);
  };

  return (
    <Dialog open={!!pallet} onOpenChange={open => { if (!open && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("recv.count_title")}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t("recv.count_body")}</p>
        </DialogHeader>

        {loadFailed && <p className="text-sm text-red-600">{t("recv.count_load_error")}</p>}
        {!loadFailed && items.length === 0 && <p className="text-sm text-slate-400">{t("common.loading")}</p>}

        {items.length > 0 && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button type="button" size="sm" variant="outline" onClick={allMatch} data-testid="count-all-match">{t("recv.count_all_match")}</Button>
            </div>
            {items.map(item => {
              const c = counts[item.id] || { good: "0", damaged: "0", note: "" };
              const good = toInt(c.good), damaged = toInt(c.damaged);
              const missing = Math.max(0, item.quantity - good - damaged);
              const extra = Math.max(0, good + damaged - item.quantity);
              return (
                <div key={item.id} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`count-item-${item.id}`}>
                  {/* The full name wraps over as many lines as it needs; the ASIN, tenant and declared count sit on their own row. */}
                  <p className="text-sm font-semibold leading-snug text-slate-800 break-words">{item.product_name}</p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="min-w-0 truncate text-[11px] text-slate-500"><span className="font-mono">{item.asin}</span> · {tenantName(item.tenant_id)}</p>
                    <span className="text-sm font-bold text-slate-700 shrink-0">{t("disc.declared")} {item.quantity}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-slate-500 space-y-1">{t("disc.good")}
                      <Input inputMode="numeric" value={c.good} onChange={e => setCount(item.id, { good: e.target.value.replace(/\D/g, "") })}
                        onFocus={e => e.target.select()} className="h-10" />
                    </label>
                    <label className="text-xs text-slate-500 space-y-1">{t("disc.damaged")}
                      <Input inputMode="numeric" value={c.damaged} onChange={e => setCount(item.id, { damaged: e.target.value.replace(/\D/g, "") })}
                        onFocus={e => e.target.select()} className="h-10" />
                    </label>
                  </div>
                  {(missing > 0 || extra > 0 || damaged > 0) && (
                    <>
                      <p className="text-xs font-semibold text-amber-700">
                        {missing > 0 && t("recv.count_short", { count: missing })}
                        {missing > 0 && extra > 0 && " · "}
                        {extra > 0 && t("recv.count_over", { count: extra })}
                        {damaged > 0 && (missing > 0 || extra > 0) && " · "}
                        {damaged > 0 && `${t("disc.damaged")} ${damaged}`}
                      </p>
                      <Input value={c.note} maxLength={500} onChange={e => setCount(item.id, { note: e.target.value })}
                        placeholder={t("recv.note_placeholder")} className="h-10" />
                    </>
                  )}
                </div>
              );
            })}

            {unexpected.length > 0 && <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1">{t("recv.count_unexpected")}</p>}
            {unexpected.map(row => (
              <div key={row.key} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                <div className="flex gap-2">
                  <Select value={row.tenant_id} onValueChange={v => patchUnexpected(row.key, { tenant_id: v })}>
                    <SelectTrigger className="h-10 flex-1" aria-label={t("recv.count_tenant")}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(pallet?.works ?? []).map(w => <SelectItem key={w.tenant_id} value={w.tenant_id}>{w.tenant_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-red-500" aria-label={t("recv.count_remove")}
                    onClick={() => setUnexpected(rows => rows.filter(r => r.key !== row.key))}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="relative">
                  <Input value={row.name} onChange={e => searchProduct(row.key, e.target.value)} placeholder={t("recv.count_product")} className="h-10" />
                  {row.results.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
                      {row.results.map(p => (
                        <button key={p.asin} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                          onClick={() => patchUnexpected(row.key, { asin: p.asin, name: `${p.product_name} (${p.asin})`, results: [] })}>
                          {p.product_name} <span className="font-mono text-xs text-slate-400">{p.asin}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {!row.asin && row.name.trim().length >= 2 && row.results.length === 0 && (
                    <p className="mt-1 text-xs text-slate-400">{t("recv.count_no_product")}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs text-slate-500 space-y-1">{t("disc.good")}
                    <Input inputMode="numeric" value={row.good} onChange={e => patchUnexpected(row.key, { good: e.target.value.replace(/\D/g, "") })} className="h-10" />
                  </label>
                  <label className="text-xs text-slate-500 space-y-1">{t("disc.damaged")}
                    <Input inputMode="numeric" value={row.damaged} onChange={e => patchUnexpected(row.key, { damaged: e.target.value.replace(/\D/g, "") })} className="h-10" />
                  </label>
                </div>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={addUnexpected} data-testid="count-add-unexpected">
              <Plus className="w-3.5 h-3.5" /> {t("recv.count_add_unexpected")}
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy || items.length === 0 || !unexpectedValid}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-receive">
            {t("recv.receive_pallet")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
